import React, { useCallback } from 'react';
import { FormikValues, useField, useFormikContext } from 'formik';
import Select, { SelectProps, OptionProps, SelectValue } from 'antd/lib/select';
import { useFormikFormContext } from '../FormikFormContext';
import { IExtraInputProps, useInputProps } from '../hooks/useInputProps';

export interface SelectOption extends OptionProps {
  label: React.ReactNode;
}

export interface SelectInputProps<T extends FormikValues>
  extends Omit<SelectProps<any>, 'name'>,
    IExtraInputProps {
  name: keyof T & string;
  onChange?: (value: SelectValue, option: any) => void;
  options?: SelectOption[];
  children?: React.ReactNode;
}

/**
 * Campo per input di tipo select, con opzioni fornibili direttamente.
 */
export function SelectInput<T extends FormikValues>(
  props: SelectInputProps<T>
) {
  const {
    name,
    options,
    children,
    onChange,
    extraOptions,
    ...otherProps
  } = props;
  const [field, , helper] = useField(name);
  const inputProps = useInputProps(props);
  const handleChange = useCallback(
    (value: SelectValue, option: any) => {
      helper.setValue(value ?? null);
      if (onChange) onChange(value, option);
    },
    [helper, onChange]
  );
  const handleBlur = useCallback(() => helper.setTouched(true), [helper]);

  return (
    <Select
      {...otherProps}
      {...field}
      onChange={handleChange}
      onBlur={handleBlur}
      {...inputProps}
    >
      {options &&
        options.map(option => (
          <Select.Option key={option.value} {...option}>
            {option.label}
          </Select.Option>
        ))}
      {children}
    </Select>
  );
}
